/**
 * TDBLeaderPulse — the 60-second TDB leader read.
 *
 * Renders FIVE pulse sections that share a single backend round-trip
 * (`GET /api/tdb/leader-pulse`):
 *
 *   1. Health Pulse · GOP MTD · MIS freshness · city race · top alert
 *   2. Operations Pulse · yesterday vs day-before revenue · weekend
 *      mix MTD · top SKUs (or unlock note)
 *   3. Accountability Strip · Aditya / Roji / Nihal · last weekly
 *      update / MIS upload / cost entry
 *   4. 24h Changelog · what moved in the last 24 hours
 *   5. Catalogue Intelligence · 389 products · recipe coverage ·
 *      price-tier distribution · premium top 5
 *
 * Sits between the Missing-Days Ticker and the Channel Matrix on the
 * TDB DivisionDetail page. All 5 sections collapse onto a 1-column
 * layout on mobile (< 640px) and expand to 2-col / 3-col on desktop.
 *
 * Hot-refresh: 60s default interval, plus a manual Refresh button.
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import {
  Activity, AlertTriangle, CheckCircle2, Clock, Package, RefreshCw,
  TrendingUp, TrendingDown, Users, Calendar, Award, Zap,
} from 'lucide-react';

const fINR = (n) => {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v.toFixed(0)}`;
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' });
  } catch { return iso; }
};

const daysSince = (iso) => {
  if (!iso) return null;
  try {
    const ms = Date.now() - new Date(iso).getTime();
    return Math.floor(ms / 86400000);
  } catch { return null; }
};

const levelTone = (level) => {
  switch (level) {
    case 'red':   return { bg: '#FCEBEB', fg: '#A32D2D', border: '#E8B4B4' };
    case 'amber': return { bg: '#FEF6E4', fg: '#92400E', border: '#FACC15' };
    case 'green': return { bg: '#E8F7F4', fg: '#0F766E', border: '#BAE7DC' };
    default:      return { bg: '#F7F5F2', fg: '#6B7280', border: '#E5E1DC' };
  }
};

export default function TDBLeaderPulse() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const r = await api.get('/tdb/leader-pulse');
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 60000);
    return () => clearInterval(id);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="rounded-md border border-dashed px-3 py-2 text-xs text-[#9CA3AF]"
        style={{ borderColor: '#E5E1DC' }} data-testid="tdb-pulse-loading">
        Loading 60-second TDB leader pulse…
      </div>
    );
  }
  if (data?.forbidden) return null;
  if (error) {
    return (
      <div className="rounded-md px-3 py-2 text-xs"
        style={{ backgroundColor: '#FCEBEB', color: '#A32D2D', border: '1px solid #E8B4B4' }}
        data-testid="tdb-pulse-error">
        Leader-pulse error: {error}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-3" data-testid="tdb-leader-pulse">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity size={16} style={{ color: '#185FA5' }} />
          <h2 className="text-sm font-semibold text-[#1B2A4A]">TDB · 60-Second Leader Pulse</h2>
          <span className="text-[10px] text-[#9CA3AF]">as of {fmtDateTime(data.as_of)}</span>
        </div>
        <button onClick={() => { setRefreshing(true); load(true); }} disabled={refreshing}
          className="text-[11px] flex items-center gap-1 px-2 py-1 rounded border hover:bg-[#F7F5F2] disabled:opacity-50"
          style={{ borderColor: '#E5E1DC', color: '#185FA5' }}
          data-testid="tdb-pulse-refresh">
          <RefreshCw size={11} className={refreshing ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* === HEALTH PULSE · 4 tiles === */}
      <HealthPulse h={data.health} />

      {/* === OPERATIONS + ACCOUNTABILITY 2-col row === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <OperationsPulse ops={data.ops} />
        <AccountabilityStrip acct={data.accountability} />
      </div>

      {/* === CHANGELOG + CATALOGUE 2-col row === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Changelog24h cl={data.changelog_24h} />
        <CatalogueIntel cat={data.catalogue} />
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// HEALTH PULSE — 4 tiles · GOP · MIS freshness · City race · Alert
// ───────────────────────────────────────────────────────────────
function HealthPulse({ h }) {
  const navigate = useNavigate();
  if (!h) return null;
  const gopTone = levelTone(h.gop?.level);
  const misTone = levelTone(h.mis_freshness?.level);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="tdb-health-pulse">
      {/* GOP MTD */}
      <PulseTile
        title="GOP · latest MIS period"
        icon={<TrendingUp size={13} />}
        tone={gopTone}
        testid="health-gop-tile">
        {h.gop?.available ? (
          <>
            <div className="text-lg font-bold tabular-nums" style={{ color: gopTone.fg }}>
              {fINR(h.gop.gop)} <span className="text-sm font-semibold">({h.gop.gop_pct}%)</span>
            </div>
            <div className="text-[10px] text-[#6B7280]">
              Income {fINR(h.gop.income)} · Expense {fINR(h.gop.expense)}
            </div>
            <div className="text-[10px] text-[#6B7280] mt-0.5">
              {h.gop.period_start} → {h.gop.period_end}
            </div>
          </>
        ) : (
          <div className="text-xs text-[#9CA3AF] italic">No MIS uploaded — Nihal needs to upload monthly P&L.</div>
        )}
      </PulseTile>

      {/* MIS Freshness */}
      <PulseTile
        title="MIS data freshness"
        icon={<Clock size={13} />}
        tone={misTone}
        testid="health-mis-tile">
        {h.mis_freshness?.available ? (
          <>
            <div className="text-lg font-bold tabular-nums" style={{ color: misTone.fg }}>
              {h.mis_freshness.age_days < 0 ? '—' : `${h.mis_freshness.age_days}d ago`}
            </div>
            <div className="text-[10px] text-[#6B7280]">
              by {h.mis_freshness.uploaded_by}
            </div>
            <div className="text-[10px] text-[#6B7280] mt-0.5 truncate">
              {h.mis_freshness.source_file_name}
            </div>
          </>
        ) : (
          <div className="text-xs text-[#9CA3AF] italic">No MIS uploaded yet.</div>
        )}
      </PulseTile>

      {/* City race · most recent complete day */}
      <PulseTile
        title={`City race · ${fmtDate(h.city_race_recent_date)}`}
        icon={<Award size={13} />}
        tone={{ bg: '#EEF4FA', fg: '#185FA5', border: '#B5D4F4' }}
        testid="health-city-race-tile">
        {h.city_race_recent ? (
          <>
            <div className="text-sm font-bold text-[#0F766E]">
              {h.city_race_recent.best.city} leads · {fINR(h.city_race_recent.best.rev)}
            </div>
            <div className="text-[11px] text-[#A32D2D]">
              {h.city_race_recent.worst.city} ↓ {fINR(h.city_race_recent.worst.rev)}
            </div>
            <div className="text-[10px] text-[#6B7280] mt-0.5">
              Gap: <strong>{fINR(h.city_race_recent.gap)}</strong> · MTD leader: {h.city_race_mtd?.best?.city} {fINR(h.city_race_mtd?.best?.rev)}
            </div>
          </>
        ) : null}
      </PulseTile>

      {/* Top red alert */}
      <PulseTile
        title="Top open alert"
        icon={<AlertTriangle size={13} />}
        tone={h.top_alert ? levelTone(h.top_alert.severity === 'amber' ? 'amber' : 'red') : levelTone('green')}
        testid="health-top-alert-tile"
        onClick={h.top_alert ? () => navigate('/tdb/cost') : null}>
        {h.top_alert ? (
          <>
            <div className="text-xs font-semibold leading-snug" style={{ color: levelTone(h.top_alert.severity === 'amber' ? 'amber' : 'red').fg }}>
              {h.top_alert.message}
            </div>
            <div className="text-[10px] text-[#6B7280] mt-0.5">
              {h.top_alert.city && `${h.top_alert.city} · `}{fmtDateTime(h.top_alert.triggered_at)}
            </div>
            <div className="text-[10px] text-[#185FA5] mt-1 underline">Open Cost Module →</div>
          </>
        ) : (
          <div className="text-xs text-[#0F766E] flex items-center gap-1">
            <CheckCircle2 size={11} /> No open alerts.
          </div>
        )}
      </PulseTile>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// OPERATIONS PULSE
// ───────────────────────────────────────────────────────────────
function OperationsPulse({ ops }) {
  if (!ops) return null;
  const delta = ops.revenue_delta_pct;
  const deltaTone = delta == null ? '#6B7280' : delta >= 0 ? '#0F766E' : '#A32D2D';
  const weekendShare = ops.weekend_share_pct || 0;
  return (
    <Section
      title="Operations pulse"
      icon={<Zap size={13} />}
      testid="tdb-ops-pulse">
      {/* Yesterday vs day-before */}
      <div className="grid grid-cols-2 gap-2 mb-2.5">
        <div>
          <p className="text-[9px] uppercase tracking-widest font-semibold text-[#6B7280]">Revenue · yesterday</p>
          <p className="text-sm font-bold tabular-nums text-[#1B2A4A]">{fINR(ops.revenue_yesterday)}</p>
          <p className="text-[10px] text-[#6B7280]">{fmtDate(ops.yesterday_date)} · {ops.rows_yesterday} rows captured</p>
        </div>
        <div>
          <p className="text-[9px] uppercase tracking-widest font-semibold text-[#6B7280]">vs day before</p>
          <p className="text-sm font-bold tabular-nums flex items-center gap-1" style={{ color: deltaTone }}>
            {delta == null ? '—' : (delta >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />)}
            {delta == null ? fINR(ops.revenue_day_before) : `${delta >= 0 ? '+' : ''}${delta}%`}
          </p>
          <p className="text-[10px] text-[#6B7280]">{fmtDate(ops.day_before_date)} · {fINR(ops.revenue_day_before)}</p>
        </div>
      </div>

      {/* Weekend vs weekday mix MTD */}
      <div className="mb-2">
        <p className="text-[9px] uppercase tracking-widest font-semibold text-[#6B7280] mb-1">Weekend vs Weekday · MTD</p>
        <div className="h-2 rounded-full overflow-hidden flex" style={{ backgroundColor: '#F7F5F2' }}>
          <div className="h-full" style={{ width: `${weekendShare}%`, backgroundColor: '#92400E' }} title={`Weekend (Fri+Sat): ${weekendShare}%`} />
          <div className="h-full" style={{ width: `${100 - weekendShare}%`, backgroundColor: '#185FA5' }} title={`Weekday: ${100 - weekendShare}%`} />
        </div>
        <div className="flex items-center justify-between text-[10px] mt-1">
          <span style={{ color: '#92400E' }}>Fri+Sat {weekendShare}% · {fINR(ops.weekend_revenue_mtd)}</span>
          <span style={{ color: '#185FA5' }}>Wkdy {100 - weekendShare}% · {fINR(ops.weekday_revenue_mtd)}</span>
        </div>
      </div>

      {/* Top SKUs (or unlock note) */}
      <div>
        <p className="text-[9px] uppercase tracking-widest font-semibold text-[#6B7280] mb-0.5">Top SKUs · trailing 7d</p>
        {ops.top_skus_7d?.length ? (
          <ul className="text-[11px] space-y-0.5">
            {ops.top_skus_7d.map((s, i) => (
              <li key={i} className="flex items-center justify-between gap-2">
                <span className="truncate">{i + 1}. {s.title}</span>
                <span className="text-[#6B7280] tabular-nums">{s.qty}× · {fINR(s.revenue)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[10px] italic text-[#9CA3AF]">{ops.top_skus_note}</p>
        )}
      </div>
    </Section>
  );
}

// ───────────────────────────────────────────────────────────────
// ACCOUNTABILITY STRIP
// ───────────────────────────────────────────────────────────────
function AccountabilityStrip({ acct }) {
  if (!acct?.leaders?.length) return null;
  return (
    <Section
      title="Whose ball is it"
      icon={<Users size={13} />}
      testid="tdb-accountability-strip">
      <div className="space-y-1.5">
        {acct.leaders.map((l) => {
          const wuAge = daysSince(l.last_weekly_update_at);
          const wuTone = wuAge == null ? 'gray' : wuAge > 14 ? 'red' : wuAge > 7 ? 'amber' : 'green';
          const ceAge = daysSince(l.last_cost_entry_at);
          const misAge = daysSince(l.last_mis_upload_at);
          return (
            <div key={l.email} className="flex items-center justify-between gap-2 py-1 border-b border-[#F0EBE5] last:border-0"
              data-testid={`accountability-row-${l.name.toLowerCase()}`}>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#1B2A4A] truncate">{l.name}</p>
                <p className="text-[10px] text-[#6B7280]">{l.role}</p>
              </div>
              <div className="flex flex-col items-end gap-0.5 text-[10px] tabular-nums">
                {l.last_weekly_update_at && (
                  <span className="flex items-center gap-1" style={{ color: wuTone === 'red' ? '#A32D2D' : wuTone === 'amber' ? '#92400E' : '#0F766E' }}>
                    <Calendar size={9} /> Weekly: {wuAge}d ago
                  </span>
                )}
                {!l.last_weekly_update_at && (
                  <span className="text-[10px] text-[#A32D2D]">No weekly update on file</span>
                )}
                {l.name === 'Nihal' && (
                  <span className="text-[#6B7280]">MIS: {misAge != null ? `${misAge}d ago` : '—'}</span>
                )}
                {ceAge != null && (
                  <span className="text-[#6B7280]">Cost entry: {ceAge}d ago</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

// ───────────────────────────────────────────────────────────────
// 24h CHANGELOG
// ───────────────────────────────────────────────────────────────
function Changelog24h({ cl }) {
  if (!cl) return null;
  const items = [
    { label: 'Daily sales entries', count: cl.daily_sales_entries, color: '#185FA5' },
    { label: 'Cost entries',        count: cl.cost_entries,        color: '#2A7B88' },
    { label: 'Invoices scanned',    count: cl.invoices_scanned,    color: '#92400E' },
    { label: 'MIS uploads',         count: cl.mis_uploads,         color: '#7C3AED' },
    { label: 'Alerts created',      count: cl.alerts_created,      color: '#A32D2D' },
    { label: 'Mira actions',        count: cl.mira_actions,        color: '#0F766E' },
  ];
  return (
    <Section
      title={`Last 24h activity · ${cl.total} events`}
      icon={<Clock size={13} />}
      testid="tdb-changelog-24h">
      <div className="grid grid-cols-3 gap-2">
        {items.map((it) => (
          <div key={it.label} className="rounded-md p-2 border" style={{ borderColor: '#E5E1DC', backgroundColor: '#FAFAF7' }}>
            <p className="text-base font-bold tabular-nums" style={{ color: it.color }}>{it.count}</p>
            <p className="text-[9px] uppercase tracking-wider font-medium text-[#6B7280] leading-tight">{it.label}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ───────────────────────────────────────────────────────────────
// CATALOGUE INTEL
// ───────────────────────────────────────────────────────────────
function CatalogueIntel({ cat }) {
  if (!cat?.available) {
    return (
      <Section title="Catalogue intelligence" icon={<Package size={13} />} testid="tdb-catalogue-intel">
        <p className="text-xs italic text-[#9CA3AF]">{cat?.note || 'No catalogue data.'}</p>
      </Section>
    );
  }
  const cov = cat.recipe_coverage_pct || 0;
  const covTone = cov < 30 ? '#A32D2D' : cov < 70 ? '#92400E' : '#0F766E';
  const boston = cat.boston_matrix;
  return (
    <Section title="Catalogue intelligence" icon={<Package size={13} />} testid="tdb-catalogue-intel">
      {/* Recipe coverage */}
      <div className="mb-2">
        <div className="flex items-center justify-between text-[10px] mb-0.5">
          <span className="text-[#6B7280] uppercase tracking-widest font-semibold">Recipe coverage</span>
          <span className="tabular-nums font-semibold" style={{ color: covTone }}>{cov}% · {cat.recipes_set} / {cat.total_products}</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: '#F7F5F2' }}>
          <div className="h-full" style={{ width: `${cov}%`, backgroundColor: covTone }} />
        </div>
        {cov < 30 && (
          <p className="text-[10px] mt-1 text-[#A32D2D]">
            Only {cat.recipes_set} of {cat.total_products} products have recipes — set them to upgrade the Boston matrix Y-axis from price-tier to true margin.
          </p>
        )}
      </div>

      {/* Boston-matrix mini-preview when available · the full matrix is
          rendered below the Leader Pulse as <TDBProductBostonMatrix />. */}
      {boston?.available && (
        <div className="mb-2 rounded-md p-2" style={{ backgroundColor: '#FAFAF7', border: '1px solid #E5E1DC' }}
          data-testid="catalogue-boston-preview">
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-[#6B7280] uppercase tracking-widest font-semibold">Boston matrix · 30d</span>
            <span className="text-[#185FA5] tabular-nums">{boston.totals?.unique_products} products · {boston.window_start} → {boston.window_end}</span>
          </div>
          <div className="grid grid-cols-2 gap-1 text-[10px]">
            <Quadrant label="Stars"     count={boston.quadrant_counts?.stars}     color="#0F766E" testid="cat-boston-stars" />
            <Quadrant label="Puzzles"   count={boston.quadrant_counts?.puzzles}   color="#92400E" testid="cat-boston-puzzles" />
            <Quadrant label="Cash Cows" count={boston.quadrant_counts?.cash_cows} color="#185FA5" testid="cat-boston-cash_cows" />
            <Quadrant label="Dogs"      count={boston.quadrant_counts?.dogs}      color="#A32D2D" testid="cat-boston-dogs" />
          </div>
          {boston.top_stars?.[0] && (
            <p className="text-[10px] mt-1 text-[#6B7280]">
              ⭐ Top star: <strong className="text-[#0F766E]">{boston.top_stars[0].name}</strong> · {boston.top_stars[0].units}u
            </p>
          )}
        </div>
      )}

      {/* Price tiers */}
      <div className="mb-2">
        <p className="text-[10px] text-[#6B7280] uppercase tracking-widest font-semibold mb-1">Price tiers</p>
        <div className="grid grid-cols-4 gap-1 text-[10px]">
          {[
            { k: 'under_500', label: '<₹500' },
            { k: '500_999', label: '₹500–999' },
            { k: '1000_2499', label: '₹1K–2.5K' },
            { k: '2500_plus', label: '₹2.5K+' },
          ].map(({ k, label }) => (
            <div key={k} className="rounded p-1.5" style={{ backgroundColor: '#FAFAF7', border: '1px solid #E5E1DC' }}>
              <p className="font-bold text-sm text-[#1B2A4A] tabular-nums">{cat.price_tiers?.[k] ?? 0}</p>
              <p className="text-[#6B7280]">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Top product types */}
      {cat.top_types?.length > 0 && (
        <div className="mb-1.5">
          <p className="text-[10px] text-[#6B7280] uppercase tracking-widest font-semibold mb-0.5">Top product types</p>
          <div className="flex flex-wrap gap-1">
            {cat.top_types.slice(0, 6).map((t) => (
              <span key={t.type} className="text-[10px] px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: '#EEF4FA', color: '#185FA5' }}>
                {t.type} <strong>{t.count}</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {cat.boston_matrix_note && (
        <p className="text-[10px] italic text-[#9CA3AF] mt-1">{cat.boston_matrix_note}</p>
      )}
    </Section>
  );
}

function Quadrant({ label, count, color, testid }) {
  return (
    <div className="rounded px-1.5 py-1 border" style={{ borderColor: '#E5E1DC' }} data-testid={testid}>
      <p className="text-[9px] uppercase tracking-wider text-[#6B7280]">{label}</p>
      <p className="text-sm font-bold tabular-nums" style={{ color }}>{count ?? 0}</p>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────
// Shared section + tile primitives
// ───────────────────────────────────────────────────────────────
function Section({ title, icon, testid, children }) {
  return (
    <div className="rounded-[10px] bg-white border p-3"
      style={{ borderColor: '#E5E1DC' }}
      data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2">
        <span style={{ color: '#185FA5' }}>{icon}</span>
        <p className="text-[11px] uppercase tracking-widest font-semibold text-[#1B2A4A]">{title}</p>
      </div>
      {children}
    </div>
  );
}

function PulseTile({ title, icon, tone, children, testid, onClick }) {
  return (
    <div onClick={onClick}
      role={onClick ? 'button' : undefined}
      className={`rounded-[10px] p-3 border ${onClick ? 'cursor-pointer hover:opacity-90 transition' : ''}`}
      style={{ backgroundColor: tone.bg, borderColor: tone.border }}
      data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <span style={{ color: tone.fg }}>{icon}</span>
        <p className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: tone.fg }}>{title}</p>
      </div>
      {children}
    </div>
  );
}
